import { FlexBox, Skeleton } from '@dixa/lib-shared-habla';
import { AnalyticsMenu, AnalyticsMenuActions } from 'lib-analytics-ui';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { Media } from 'lib-shared-types-channels';
import { memo, useMemo } from 'react';

import { AppLink } from 'components/appLink';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { useChannelDashboardChannelContext } from '../../channelDashboardChannel.context';

export const ChannelDashboardChannelActions = memo(
  function ChannelDashboardChannelActions() {
    const { media, channels, isSkeleton } = useChannelDashboardChannelContext();
    const flags = useFeatureFlag(['custom-analytics']);

    const hasAnalyticsAccess = useHasAccess('analytics');

    const actions = useMemo(
      (): AnalyticsMenuActions => [
        {
          type: 'link',
          icon: 'conversations',
          label: 'See all conversations',
          link: (
            <AppLink
              path="/conversations"
              params={{
                search: {
                  channel: channels,
                },
              }}
            />
          ),
          onClick: () => {
            ampli.dashboardChannelAllConversationsClicked();
          },
        },
        ...(flags.customAnalytics && hasAnalyticsAccess
          ? ([
              {
                type: 'link',
                icon: 'chart-bar',
                label: 'Go to Analytics',
                link: (
                  <AppLink
                    path="/analytics/dashboard/:slug"
                    params={{
                      path: { slug: channelMediaToAnalyticsSlug[media] },
                      search: {
                        period: 'today',
                      },
                    }}
                  />
                ),
                onClick: () => {
                  ampli.dashboardChannelGoToAnalyticsClicked();
                },
              },
            ] satisfies AnalyticsMenuActions)
          : []),
        {
          type: 'link',
          icon: 'presentation',
          label: 'TV mode',
          link: (
            <AppLink
              path="/tv/dashboard/channels/:media"
              params={{
                path: { media },
              }}
            />
          ),
          onClick: () => {
            ampli.dashboardChannelTvDashboardClicked();
          },
        },
      ],
      [channels, flags.customAnalytics, hasAnalyticsAccess, media],
    );

    return (
      <>
        {isSkeleton ? (
          <FlexBox
            justifyContent="center"
            alignItems="center"
            width="32px"
            height="32px"
          >
            <Skeleton height="18px" width="6px" />
          </FlexBox>
        ) : (
          <AnalyticsMenu actions={actions} />
        )}
      </>
    );
  },
);

/**
 * Maps the channel media types to the names we use for the dashboards in
 * Analytics in order to deep link to the corresponding Analytics dashboard.
 */
const channelMediaToAnalyticsSlug: Record<Media, string> = {
  speak: 'voice',
  email: 'email',
  interactivechat: 'chat',
  messaging: 'messaging',
};
